import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import useSettingsParams from "hooks/useSettingsParams";
import { useAuth } from "context/AuthContext";
import { hidesPurchasePrices } from "utils/helpers/access/hasUserAccess";
import { useRegistryColumns } from "utils/helpers/list/useRegistryColumns";
import {
    loadStockFilter,
    saveStockFilter,
} from "../model/stockFiltersStorage";

export type WarehouseColumnId = 'category' | 'article' | 'updated' | 'lots' | 'credit';

type WarehouseOperationType = {
    warehouseId?: number;
    productId?: number;
    stockCount?: number;
} | null;

type WarehouseContextType = {
    showSpinner: boolean;
    setShowSpinner: Dispatch<SetStateAction<boolean>>;
    stocksOrder: string | false;
    setStocksOrder: Dispatch<SetStateAction<string | false>>;
    stocksDesc: boolean;
    setStocksDesc: Dispatch<SetStateAction<boolean>>;
    wOperation: WarehouseOperationType;
    setWOperation: Dispatch<SetStateAction<WarehouseOperationType>>;
    stockFilter: Record<string, any> | null;
    setStockFilter: Dispatch<SetStateAction<Record<string, any> | null>>;
    showCategory: boolean;
    showArticle: boolean;
    showUpdated: boolean;
    showCredit: boolean;
    showLotBreakdown: boolean;
    toggleListColumn: (id: WarehouseColumnId) => void;
    listResetNonce: number;
    triggerListReset: () => void;
    listRefreshNonce: number;
    triggerListRefresh: () => void;
};

const WarehouseContext = createContext<WarehouseContextType | null>(null);
export const useWarehouse = (): WarehouseContextType => {
    const context = useContext(WarehouseContext);
    if (!context) {
        throw new Error("useWarehouse must be used within WarehouseProvider");
    }
    return context;
};
 
export const WarehouseProvider = ({ children }: { children: ReactNode }) => {
    const { auth } = useAuth();
    const params = useSettingsParams();
    const userId = Number(auth?.user?.ID || 0);
    const [ showSpinner, setShowSpinner ] = useState(false);
    const [ stocksOrder, setStocksOrder ] = useState<string | false>('');
    const [ stocksDesc, setStocksDesc ] = useState(false);
    const [ wOperation, setWOperation ] = useState<WarehouseOperationType>(null);
    const [ stockFilter, setStockFilter ] = useState<Record<string, any> | null>(() => loadStockFilter(userId));
    const [ listResetNonce, setListResetNonce ] = useState(0);
    const [ listRefreshNonce, setListRefreshNonce ] = useState(0);
    const columnDefs = useMemo(() => ([
        { id: 'category' as const, defaultVisible: false },
        { id: 'article' as const, defaultVisible: false },
        { id: 'updated' as const, defaultVisible: false },
        { id: 'lots' as const, defaultVisible: false },
        {
            id: 'credit' as const,
            defaultVisible: false,
            legacyKey: `voltox-${userId}-warehouse-show-credit`,
        },
    ]), [userId]);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        `registryColumns:warehouse:${userId || 0}`,
        columnDefs,
    );
    const canShowLots = auth?.user?.access?.role === 'manager' && !params.ignoreWarehousePrice && !hidesPurchasePrices(auth?.user);
    const showCategory = Boolean(listColumns.category);
    const showArticle = Boolean(listColumns.article);
    const showUpdated = Boolean(listColumns.updated);
    const showLotBreakdown = Boolean(canShowLots && listColumns.lots);
    const showCredit = Boolean(params.creditControl && listColumns.credit);

    useEffect(() => {
        if (!userId) return;
        saveStockFilter(userId, stockFilter);
    }, [stockFilter, userId]);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    const values = {
        showSpinner, setShowSpinner,
        stocksOrder, setStocksOrder,
        stocksDesc, setStocksDesc,
        wOperation, setWOperation,
        stockFilter, setStockFilter,
        showCategory,
        showArticle,
        showUpdated,
        showCredit,
        showLotBreakdown,
        toggleListColumn,
        listResetNonce, triggerListReset,
        listRefreshNonce, triggerListRefresh,
    }

    return (
        <WarehouseContext.Provider value={values}>
            {children}
        </WarehouseContext.Provider>
    )
}
